import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

type CloseDialog = () => void;

const DialogRegistryContext = createContext<{
  registerDialog: (closeDialog: CloseDialog) => () => void;
  closeAllOpenDialogs: () => void;
} | null>(null);

export function DialogRegistryProvider({ children }: React.PropsWithChildren) {
  const dialogsRef = useRef(new Set<CloseDialog>());

  const registerDialog = useCallback((closeDialog: CloseDialog) => {
    dialogsRef.current.add(closeDialog);

    return () => {
      dialogsRef.current.delete(closeDialog);
    };
  }, []);

  const closeAllOpenDialogs = useCallback(() => {
    dialogsRef.current.forEach((closeDialog) => {
      closeDialog();
    });
  }, []);

  const value = useMemo(
    () => ({ registerDialog, closeAllOpenDialogs }),
    [registerDialog, closeAllOpenDialogs],
  );

  return (
    <DialogRegistryContext.Provider value={value}>
      {children}
    </DialogRegistryContext.Provider>
  );
}

export function useDialogRegistry() {
  const context = useContext(DialogRegistryContext);

  if (!context) {
    throw new Error(
      "useDialogRegistry must be used within a DialogRegistryProvider",
    );
  }

  return context;
}
